import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import {
  FormRow,
  FormLabel,
  FormFieldWrapper,
  FormRowProps
} from '../form-input';
import { SelectField } from '.';

type CornersType =
  | 'rounded'
  | 'square'
  | 'roundedPopupSquareElements'
  | 'squarePopupRoundElements';

type CornersValue = {
  borderRadius: number;
  popupBorderRadius: number;
};

export interface RoundedCornersFieldProps extends FormRowProps {
  label: string;
  onChange: (value: CornersValue) => void;
  value?: Partial<CornersValue>;
}

export function RoundedCornersField(props: RoundedCornersFieldProps) {
  const { value, onChange, ...formRowProps } = props;

  const { t } = useTranslation();

  const toValues = useCallback((corners: CornersType): CornersValue => {
    switch (corners) {
      case 'rounded':
        return { borderRadius: 5, popupBorderRadius: 12 };

      case 'square':
        return { borderRadius: 0, popupBorderRadius: 0 };

      case 'roundedPopupSquareElements':
        return { borderRadius: 0, popupBorderRadius: 12 };

      case 'squarePopupRoundElements':
        return { borderRadius: 5, popupBorderRadius: 0 };
    }
  }, []);

  const updateCorners = useCallback(
    (e: SelectChangeEvent<string>) => {
      const newCornerTypes = e.target.value as CornersType;

      const newCornerValues = toValues(newCornerTypes);

      onChange(newCornerValues);
    },
    [value, onChange, toValues]
  );

  // Using corners as a string (instead of an object) to allow an option to be compared as "selected"
  const corners = useMemo(() => {
    // its square if radius is 0
    if (value?.borderRadius === 0 && value?.popupBorderRadius === 0) {
      return 'square';
    }
    // popup is rounded but elements are square
    if (value?.borderRadius === 0 && value?.popupBorderRadius !== 0) {
      return 'roundedPopupSquareElements';
    }

    // popup is square but elements are rounded
    if (value?.borderRadius !== 0 && value?.popupBorderRadius === 0) {
      return 'squarePopupRoundElements';
    }

    // considering both are rounded
    return 'rounded';
  }, [value?.borderRadius, value?.popupBorderRadius]);

  return (
    <SelectField
      value={corners}
      onChange={updateCorners}
      options={[
        {
          value: 'square',
          label: t('square')
        },
        {
          value: 'rounded',
          label: t('rounded')
        },
        {
          value: 'roundedPopupSquareElements',
          label: t('roundedPopupSquareElements')
        },
        {
          value: 'squarePopupRoundElements',
          label: t('squarePopupRoundElements')
        }
      ]}
      {...formRowProps}
    />
  );
}
